import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('pushpanjali_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [systemConfig, setSystemConfig] = useState({
    delivery_fee: 40,
    min_order_value: 0,
    free_delivery_threshold: 500,
    tax_percentage: 5,
    show_tax_and_delivery: true,
    is_online: true,
    allow_preorders: true,
    operating_hours: {},
    is_currently_open: true,
    next_opening: null,
  });

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      setSystemConfig(response.data);
    } catch (e) {
      console.error('Failed to fetch public settings', e);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    localStorage.setItem('pushpanjali_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (item, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.item_id === item.id);
      if (found) {
        return prev.map((i) => i.item_id === item.id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        item_id: item.id,
        name: item.name,
        price: item.price,
        image_url: item.image_url,
        quantity: qty,
      }];
    });
  };

  const removeItem = (item_id) => setItems((prev) => prev.filter((i) => i.item_id !== item_id));

  const updateQty = (item_id, quantity) => {
    if (quantity <= 0) return removeItem(item_id);
    setItems((prev) => prev.map((i) => i.item_id === item_id ? { ...i, quantity } : i));
  };

  const clearCart = () => setItems([]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const calculateRates = () => {
    if (items.length === 0) {
      return { deliveryFee: 0, tax: 0, total: 0 };
    }
    const show = systemConfig.show_tax_and_delivery ?? true;
    if (!show) {
      return { deliveryFee: 0, tax: 0, total: subtotal };
    }
    const freeThresh = systemConfig.free_delivery_threshold ?? 500;
    const baseFee = systemConfig.delivery_fee ?? 40;
    const deliveryFee = subtotal >= freeThresh ? 0 : baseFee;
    
    const taxPct = systemConfig.tax_percentage ?? 5;
    const tax = Math.round(subtotal * (taxPct / 100) * 100) / 100;
    const total = Math.round((subtotal + tax + deliveryFee) * 100) / 100;
    
    return { deliveryFee, tax, total };
  };

  const { deliveryFee, tax, total } = calculateRates();
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQty,
      clearCart,
      subtotal,
      deliveryFee,
      tax,
      total,
      count,
      systemConfig,
      refetchConfig: fetchSettings
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
